// ─────────────────────────────────────────────────────────────────
// Glass-i — TDS / TCS Rules master page (Stage 6P.9 — Phase 5B §L1).
//
// Operator-authored CRUD over `tds_tcs_rules`. The backend is the
// single source of truth for tax-rule selection at deduction time —
// THIS PAGE NEVER COMPUTES TAX. It is purely a register surface
// over the rule table:
//
//   GET    /api/erp/tds-tcs/rules            (list)
//   POST   /api/erp/tds-tcs/rules            (create)
//   PUT    /api/erp/tds-tcs/rules/:id        (update)
//   DELETE /api/erp/tds-tcs/rules/:id        (soft-delete)
//
// Permissions (route-enforced; surface-gated here too):
//   erp.tds_tcs.rules.view    — required to load this page
//   erp.tds_tcs.rules.create  — gates "Add Rule" CTA
//   erp.tds_tcs.rules.update  — gates inline edit + status toggle
//   erp.tds_tcs.rules.delete  — gates soft-delete
//
// Brief §L1 columns:
//   Tax Type · Section · Rule Name · Applies To · Rate %
//   Higher Rate % · Threshold · Deduction Timing · Effective From
//   Effective To · Active · Actions
//
// CRITICAL — Section codes accept ANY operator string (no hardcoded
// validation). SECTION_CODES_KNOWN is a SUGGESTION list rendered via
// the AntdSelect dropdown but the operator MAY type freeform.
//
// Mirrors style:
//   frontend/src/pages/erp/purchaseReturns/PurchaseReturnsPage.jsx
//   frontend/src/pages/erp/supplierPayments/SupplierPaymentRegisterPage.jsx
// Reuses the shared GoodsReceiptsPage.css via the .grn-* wrapper.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, AutoComplete, Button, Card, DatePicker, Dropdown, Form, Input,
  InputNumber, Modal, Select, Space, Spin, Switch, Table, Tag, Tooltip,
  message
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, MoreOutlined, PlusOutlined,
  ExclamationCircleOutlined, EditOutlined, DeleteOutlined,
  PercentageOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import client from '../../../api/client';
import RoleGate from '../../../components/RoleGate';
import useAuth from '../../../hooks/useAuth';
import '../purchase/GoodsReceiptsPage.css';

const { RangePicker } = DatePicker;
const { TextArea } = Input;


// ─────────────────────────────────────────────────────────────────
// Constants — frozen mirrors of backend tdsTcs.constants.js
// (kept inline so this page has no inter-module dependency drift).
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS  = ['25', '50', '100'];
const DATE_FMT           = 'DD-MMM-YYYY';
const ISO_FMT            = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 250;

const TAX_TYPES = Object.freeze(['TDS', 'TCS']);

const APPLIES_TO = Object.freeze([
  'GOODS', 'SERVICE', 'JOBWORK', 'RENT', 'COMMISSION', 'OTHER'
]);

const RESIDENT_TYPES = Object.freeze([
  'RESIDENT', 'NON_RESIDENT', 'ANY'
]);

const THRESHOLD_BASIS = Object.freeze([
  'PER_SUPPLIER_FY', 'PER_TRANSACTION', 'NONE'
]);

const DEDUCTION_TIMINGS = Object.freeze([
  'BILL_ACCOUNTING', 'PAYMENT', 'EARLIER_OF_BILL_OR_PAYMENT'
]);

// Operator-typeable suggestion list. The runtime accepts ANY string.
const SECTION_CODES_KNOWN = Object.freeze([
  '194Q', '194C', '194J', '194H', '194I', '206C_LEGACY'
]);

const TAX_TYPE_OPTIONS = TAX_TYPES.map((v) => ({ value: v, label: v }));

const APPLIES_TO_OPTIONS = APPLIES_TO.map((v) => ({
  value: v,
  label: v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}));

const RESIDENT_TYPE_OPTIONS = RESIDENT_TYPES.map((v) => ({
  value: v,
  label: v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}));

const THRESHOLD_BASIS_OPTIONS = THRESHOLD_BASIS.map((v) => ({
  value: v,
  label: v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}));

const TIMING_OPTIONS = DEDUCTION_TIMINGS.map((v) => ({
  value: v,
  label: v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}));

const SECTION_CODE_OPTIONS = SECTION_CODES_KNOWN.map((v) => ({
  value: v, label: v
}));


// ─────────────────────────────────────────────────────────────────
// Pure helpers — local so the page has no formatter import.
// ─────────────────────────────────────────────────────────────────

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
}

function fmtMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  try {
    return n.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } catch {
    return n.toFixed(2);
  }
}

function fmtPct(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  // Up to 4dp, trim trailing zeros for display calm.
  return `${n.toFixed(4).replace(/\.?0+$/, '')}%`;
}

function pickField(row, snake, camel) {
  if (!row) return undefined;
  if (camel in row && row[camel] !== undefined && row[camel] !== null) return row[camel];
  if (snake in row && row[snake] !== undefined && row[snake] !== null) return row[snake];
  return undefined;
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

function timingLabel(v) {
  if (!v) return '—';
  return String(v).replace(/_/g, ' ');
}


// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function TdsTcsRulesPage() {
  const { me } = useAuth();
  const [form] = Form.useForm();

  // Wildcard '*' is the master role; explicit
  // `erp.tds_tcs.rules.*` strings are the Stage 6P.9 perms.
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView   = hasPerm('erp.tds_tcs.rules.view');
  const canCreate = hasPerm('erp.tds_tcs.rules.create');
  const canUpdate = hasPerm('erp.tds_tcs.rules.update');
  const canDelete = hasPerm('erp.tds_tcs.rules.delete');

  // ── Toolbar state ────────────────────────────────────────────
  const [taxTypeFilter, setTaxTypeFilter] = useState(null);
  const [activeFilter,  setActiveFilter]  = useState(null);
  const [dateRange,     setDateRange]     = useState(null);
  const [searchInput,   setSearchInput]   = useState('');
  const [search,        setSearch]        = useState('');

  // ── Server pagination state ──────────────────────────────────
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total,    setTotal]    = useState(0);

  // ── Data state ───────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId,  setBusyId]  = useState(null);

  // ── Modal state ──────────────────────────────────────────────
  const [modalOpen,    setModalOpen]    = useState(false);
  const [editingRow,   setEditingRow]   = useState(null);
  const [submitting,   setSubmitting]   = useState(false);

  // ── Debounced search wiring ──────────────────────────────────
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch((prev) => {
        if (prev === searchInput) return prev;
        setPage(1);
        return searchInput;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = { page, pageSize };
      const needle = (search || '').trim();
      if (needle) {
        params.q      = needle;
        params.search = needle;
      }
      if (taxTypeFilter) params.taxType = taxTypeFilter;
      if (activeFilter === 'active')   params.isActive = true;
      if (activeFilter === 'inactive') params.isActive = false;
      if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
        params.effectiveFrom = dateRange[0].format(ISO_FMT);
        params.effectiveTo   = dateRange[1].format(ISO_FMT);
      }

      const resp = await client.get('/api/erp/tds-tcs/rules', { params });
      const out  = unwrap(resp);
      const list = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : Array.isArray(out?.items) ? out.items
        : [];
      const count = Array.isArray(out) ? list.length
        : Number(out?.total ?? out?.count ?? list.length) || 0;

      setRows(list);
      setTotal(count);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load TDS/TCS rules.'),
        key: 'tt-rules-list'
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canView, page, pageSize, search, taxTypeFilter, activeFilter, dateRange]);

  useEffect(() => { load(); }, [load]);

  // ── Toolbar handlers — any filter change resets page to 1 ────
  const onTaxTypeFilter = (v) => { setTaxTypeFilter(v || null); setPage(1); };
  const onActiveFilter  = (v) => { setActiveFilter(v || null);  setPage(1); };
  const onDates         = (v) => { setDateRange(v || null);     setPage(1); };
  const onReset = () => {
    setSearchInput('');
    setSearch('');
    setTaxTypeFilter(null);
    setActiveFilter(null);
    setDateRange(null);
    setPage(1);
  };

  // ── Modal open / close ───────────────────────────────────────
  const openAdd = useCallback(() => {
    setEditingRow(null);
    form.resetFields();
    form.setFieldsValue({
      taxType:                'TDS',
      appliesTo:              'OTHER',
      residentType:           'RESIDENT',
      thresholdBasis:         'PER_SUPPLIER_FY',
      thresholdAmount:        0,
      perTransactionThreshold:0,
      ratePercent:            0,
      higherRateNoPanPercent: 0,
      deductionTiming:        'EARLIER_OF_BILL_OR_PAYMENT',
      effectiveFrom:          dayjs(),
      effectiveTo:            null,
      isActive:               true
    });
    setModalOpen(true);
  }, [form]);

  const openEdit = useCallback((row) => {
    setEditingRow(row);
    form.resetFields();
    form.setFieldsValue({
      taxType:                pickField(row, 'tax_type',       'taxType')       || 'TDS',
      sectionCode:            pickField(row, 'section_code',   'sectionCode')   || '',
      description:            pickField(row, 'description',    'description')   || '',
      appliesTo:              pickField(row, 'applies_to',     'appliesTo')     || 'OTHER',
      residentType:           pickField(row, 'resident_type',  'residentType')  || 'RESIDENT',
      thresholdBasis:         pickField(row, 'threshold_basis','thresholdBasis')|| 'PER_SUPPLIER_FY',
      thresholdAmount:        Number(pickField(row, 'threshold_amount',          'thresholdAmount')          || 0),
      perTransactionThreshold:Number(pickField(row, 'per_transaction_threshold', 'perTransactionThreshold') || 0),
      ratePercent:            Number(pickField(row, 'rate_percent',              'ratePercent')              || 0),
      higherRateNoPanPercent: Number(pickField(row, 'higher_rate_no_pan_percent','higherRateNoPanPercent')   || 0),
      deductionTiming:        pickField(row, 'deduction_timing', 'deductionTiming') || 'EARLIER_OF_BILL_OR_PAYMENT',
      payableLedgerCode:      pickField(row, 'payable_ledger_code', 'payableLedgerCode') || '',
      expenseLedgerCode:      pickField(row, 'expense_ledger_code', 'expenseLedgerCode') || '',
      effectiveFrom:          pickField(row, 'effective_from', 'effectiveFrom')
                                ? dayjs(pickField(row, 'effective_from', 'effectiveFrom'))
                                : null,
      effectiveTo:            pickField(row, 'effective_to',   'effectiveTo')
                                ? dayjs(pickField(row, 'effective_to', 'effectiveTo'))
                                : null,
      isActive:               !!pickField(row, 'is_active', 'isActive'),
      notes:                  pickField(row, 'notes', 'notes') || ''
    });
    setModalOpen(true);
  }, [form]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingRow(null);
    form.resetFields();
  }, [form]);

  // ── Submit handler — POST or PUT depending on editing state ──
  const onSubmit = useCallback(async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (_err) {
      return; // AntD already surfaced inline errors
    }
    setSubmitting(true);
    try {
      const payload = {
        tax_type:                    values.taxType,
        section_code:                values.sectionCode,
        description:                 values.description || null,
        applies_to:                  values.appliesTo,
        resident_type:               values.residentType,
        threshold_basis:             values.thresholdBasis,
        threshold_amount:            Number(values.thresholdAmount) || 0,
        per_transaction_threshold:   Number(values.perTransactionThreshold) || 0,
        rate_percent:                Number(values.ratePercent) || 0,
        higher_rate_no_pan_percent:  Number(values.higherRateNoPanPercent) || 0,
        deduction_timing:            values.deductionTiming,
        payable_ledger_code:         values.payableLedgerCode || null,
        expense_ledger_code:         values.expenseLedgerCode || null,
        effective_from:              values.effectiveFrom
                                       ? values.effectiveFrom.format(ISO_FMT)
                                       : null,
        effective_to:                values.effectiveTo
                                       ? values.effectiveTo.format(ISO_FMT)
                                       : null,
        is_active:                   !!values.isActive,
        notes:                       values.notes || null
      };

      if (editingRow) {
        const id = pickField(editingRow, 'id', 'id');
        await client.put(
          `/api/erp/tds-tcs/rules/${encodeURIComponent(id)}`,
          payload
        );
        message.success('Rule updated.');
      } else {
        await client.post('/api/erp/tds-tcs/rules', payload);
        message.success('Rule created.');
      }
      closeModal();
      await load();
    } catch (e) {
      message.error(errMessage(
        e,
        editingRow ? 'Failed to update rule.' : 'Failed to create rule.'
      ));
    } finally {
      setSubmitting(false);
    }
  }, [form, editingRow, closeModal, load]);

  // ── Delete handler ───────────────────────────────────────────
  const runDelete = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    Modal.confirm({
      title: 'Soft-delete this rule?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <p style={{ marginBottom: 0, fontSize: 12.5 }}>
          The rule will be marked deleted and no longer considered when
          the backend resolves an applicable TDS/TCS rule. Existing
          deductions that already referenced this rule are NOT affected.
        </p>
      ),
      okText: 'Soft-delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      async onOk() {
        setBusyId(id);
        try {
          await client.delete(`/api/erp/tds-tcs/rules/${encodeURIComponent(id)}`);
          message.success('Rule soft-deleted.');
          await load();
        } catch (e) {
          message.error(errMessage(e, 'Failed to delete rule.'));
          throw e;
        } finally {
          setBusyId(null);
        }
      }
    });
  }, [load]);

  // ── Row-action menu builder ──────────────────────────────────
  const buildRowMenu = useCallback((row) => {
    const items = [];
    if (canUpdate) {
      items.push({ key: 'edit', icon: <EditOutlined />, label: 'Edit' });
    }
    if (canDelete) {
      if (items.length) items.push({ type: 'divider' });
      items.push({
        key:    'delete',
        icon:   <DeleteOutlined />,
        label:  'Soft-delete',
        danger: true
      });
    }
    return items;
  }, [canUpdate, canDelete]);

  const onRowMenu = useCallback((key, row) => {
    switch (key) {
      case 'edit':   return openEdit(row);
      case 'delete': return runDelete(row);
      default:       return undefined;
    }
  }, [openEdit, runDelete]);

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'Tax Type',
      dataIndex: 'taxType',
      width:     90,
      render: (_v, row) => {
        const v = pickField(row, 'tax_type', 'taxType');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag
            color={v === 'TDS' ? 'blue' : 'purple'}
            className="grn-status-tag"
          >
            {v}
          </Tag>
        );
      }
    },
    {
      title:     'Section',
      dataIndex: 'sectionCode',
      width:     110,
      render: (_v, row) => {
        const v = pickField(row, 'section_code', 'sectionCode');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Rule Name',
      dataIndex: 'description',
      render: (_v, row) => {
        const v = pickField(row, 'description', 'description');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tooltip title={v}>
            <span>{v}</span>
          </Tooltip>
        );
      }
    },
    {
      title:     'Applies To',
      dataIndex: 'appliesTo',
      width:     120,
      render: (_v, row) => {
        const v = pickField(row, 'applies_to', 'appliesTo');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag className="grn-status-tag" color="default">
            {v.replace(/_/g, ' ')}
          </Tag>
        );
      }
    },
    {
      title:     'Rate %',
      dataIndex: 'ratePercent',
      width:     90,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtPct(pickField(row, 'rate_percent', 'ratePercent'))}
        </span>
      )
    },
    {
      title:     'Higher Rate %',
      dataIndex: 'higherRateNoPanPercent',
      width:     120,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtPct(pickField(row, 'higher_rate_no_pan_percent', 'higherRateNoPanPercent'))}
        </span>
      )
    },
    {
      title:     'Threshold',
      dataIndex: 'thresholdAmount',
      width:     140,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtMoney(pickField(row, 'threshold_amount', 'thresholdAmount'))}
        </span>
      )
    },
    {
      title:     'Deduction Timing',
      dataIndex: 'deductionTiming',
      width:     200,
      render: (_v, row) => {
        const v = pickField(row, 'deduction_timing', 'deductionTiming');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag className="grn-status-tag" color="default">
            {timingLabel(v)}
          </Tag>
        );
      }
    },
    {
      title:     'Effective From',
      dataIndex: 'effectiveFrom',
      width:     130,
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtDate(pickField(row, 'effective_from', 'effectiveFrom'))}
        </span>
      )
    },
    {
      title:     'Effective To',
      dataIndex: 'effectiveTo',
      width:     130,
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtDate(pickField(row, 'effective_to', 'effectiveTo'))}
        </span>
      )
    },
    {
      title:     'Active',
      dataIndex: 'isActive',
      width:     90,
      render: (_v, row) => {
        const active = !!pickField(row, 'is_active', 'isActive');
        return (
          <Tag
            color={active ? 'success' : 'default'}
            className="grn-status-tag"
          >
            {active ? 'Active' : 'Inactive'}
          </Tag>
        );
      }
    },
    {
      title:     '',
      key:       '_actions',
      width:     56,
      fixed:     'right',
      align:     'center',
      render: (_v, row) => {
        const items = buildRowMenu(row);
        const id = pickField(row, 'id', 'id');
        if (items.length === 0) return null;
        return (
          <Dropdown
            menu={{
              items,
              onClick: ({ key, domEvent }) => {
                if (domEvent && domEvent.stopPropagation) domEvent.stopPropagation();
                onRowMenu(key, row);
              }
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              className="grn-actions-btn"
              icon={<MoreOutlined />}
              loading={busyId === id}
              onClick={(e) => e.stopPropagation()}
              aria-label="Row actions"
            />
          </Dropdown>
        );
      }
    }
  ]), [buildRowMenu, onRowMenu, busyId]);

  // ── Render ───────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view TDS/TCS rules."
          description="Ask your master account to grant erp.tds_tcs.rules.view."
        />
      </div>
    );
  }

  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><PercentageOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">TDS / TCS Rules</span>
            <span className="grn-headstrip__subtitle">
              Author the TDS / TCS rate, threshold and timing rules that
              the backend consults at deduction time. Rules are matched by
              tax type + section + supplier + transaction date — the
              first active rule whose effective window covers the date
              wins. No rates are hardcoded in service code.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="grn-stat-chip">
            <strong>{total}</strong> total
          </span>
          <RoleGate>
            {canCreate ? (
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={openAdd}
              >
                Add Rule
              </Button>
            ) : null}
          </RoleGate>
        </Space>
      </div>

      {/* Sticky toolbar */}
      <div className="grn-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search section / description…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={(e) => {
            setSearchInput(e.target.value);
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 280 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All tax types"
          value={taxTypeFilter || undefined}
          onChange={onTaxTypeFilter}
          options={TAX_TYPE_OPTIONS}
          style={{ width: 140 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="Active filter"
          value={activeFilter || undefined}
          onChange={onActiveFilter}
          options={[
            { value: 'active',   label: 'Active only' },
            { value: 'inactive', label: 'Inactive only' }
          ]}
          style={{ width: 150 }}
        />
        <RangePicker
          size="small"
          value={dateRange}
          onChange={onDates}
          format={DATE_FMT}
          allowClear
          placeholder={['From', 'To']}
          style={{ width: 240 }}
        />
        <Tooltip title="Clear filters">
          <Button size="small" onClick={onReset}>Clear</Button>
        </Tooltip>
        <div className="grn-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
          />
        </Tooltip>
      </div>

      {/* Grid card */}
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r) => pickField(r, 'id', 'id') || `${pickField(r, 'tax_type', 'taxType')}-${pickField(r, 'section_code', 'sectionCode')}`}
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            onRow={(row) => ({
              onDoubleClick: () => {
                if (canUpdate) openEdit(row);
              }
            })}
            pagination={{
              current:           page,
              pageSize,
              total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   total > pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              }
            }}
            locale={{ emptyText: loading ? ' ' : 'No rules yet.' }}
          />
        </Spin>
      </Card>

      {/* Add / Edit Modal */}
      <Modal
        title={editingRow ? 'Edit TDS / TCS Rule' : 'Add TDS / TCS Rule'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={onSubmit}
        okText={editingRow ? 'Save Changes' : 'Create Rule'}
        confirmLoading={submitting}
        destroyOnClose
        width={780}
        maskClosable={false}
      >
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          requiredMark="optional"
        >
          <div className="grid grid-cols-2 gap-3">
            <Form.Item
              label="Tax Type"
              name="taxType"
              rules={[{ required: true, message: 'Tax type is required.' }]}
            >
              <Select options={TAX_TYPE_OPTIONS} />
            </Form.Item>
            <Form.Item
              label="Section Code"
              name="sectionCode"
              rules={[
                { required: true, message: 'Section code is required.' },
                { max: 20, message: 'Cannot exceed 20 characters.' }
              ]}
              tooltip="Free-form. Backend accepts any operator-typed code; suggestions are shown."
              normalize={(v) => (v ? String(v).toUpperCase() : v)}
            >
              <AutoComplete
                allowClear
                placeholder="e.g. 194Q"
                options={SECTION_CODE_OPTIONS}
                filterOption={(input, opt) =>
                  String(opt?.value || '')
                    .toLowerCase()
                    .includes(String(input || '').toLowerCase())
                }
              />
            </Form.Item>
          </div>

          <Form.Item label="Description" name="description">
            <Input maxLength={220} placeholder="Short label e.g. Purchase of Goods 0.1%" />
          </Form.Item>

          <div className="grid grid-cols-3 gap-3">
            <Form.Item label="Applies To" name="appliesTo">
              <Select options={APPLIES_TO_OPTIONS} />
            </Form.Item>
            <Form.Item label="Resident Type" name="residentType">
              <Select options={RESIDENT_TYPE_OPTIONS} />
            </Form.Item>
            <Form.Item label="Threshold Basis" name="thresholdBasis">
              <Select options={THRESHOLD_BASIS_OPTIONS} />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Form.Item label="Threshold Amount (₹)" name="thresholdAmount">
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={2}
                step={1}
              />
            </Form.Item>
            <Form.Item label="Per-Transaction Threshold (₹)" name="perTransactionThreshold">
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={2}
                step={1}
              />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Form.Item
              label="Rate Percent"
              name="ratePercent"
              rules={[
                { required: true, message: 'Rate is required.' }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={100}
                precision={4}
                step={0.0001}
                addonAfter="%"
              />
            </Form.Item>
            <Form.Item
              label="Higher Rate (no-PAN) %"
              name="higherRateNoPanPercent"
              tooltip="Applies when supplier has no valid PAN."
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={100}
                precision={4}
                step={0.0001}
                addonAfter="%"
              />
            </Form.Item>
          </div>

          <Form.Item label="Deduction Timing" name="deductionTiming">
            <Select options={TIMING_OPTIONS} />
          </Form.Item>

          <div className="grid grid-cols-2 gap-3">
            <Form.Item label="Payable Ledger Code" name="payableLedgerCode">
              <Input maxLength={40} placeholder="e.g. 194Q-TDS-PAYABLE" />
            </Form.Item>
            <Form.Item label="Expense Ledger Code" name="expenseLedgerCode">
              <Input maxLength={40} placeholder="optional override" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Form.Item
              label="Effective From"
              name="effectiveFrom"
              rules={[{ required: true, message: 'Effective-from date is required.' }]}
            >
              <DatePicker style={{ width: '100%' }} format={DATE_FMT} />
            </Form.Item>
            <Form.Item label="Effective To" name="effectiveTo">
              <DatePicker style={{ width: '100%' }} format={DATE_FMT} allowClear />
            </Form.Item>
          </div>

          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <TextArea rows={3} maxLength={4000} placeholder="Optional internal notes." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
